import { getAuthHeaders, handleAPIResponse } from '@/utils/apiUtils';

const API_BASE_URL = `${process.env.NEXT_PUBLIC_API_URL}`;

// Request types
export interface SpecificTimeSlot {
  dayOfWeek: number; // 0=Sunday, 1=Monday, ..., 6=Saturday
  startTime: string; // HH:mm format
  endTime: string; // HH:mm format
}

export interface CreateHumanInterviewBookingRequest {
  jobId: string;
  candidateIds: number[];
  stageName: string; // Used as calendar title, not for stage creation
  availableFrom: string; // ISO date
  availableTo: string; // ISO date
  slotDurationMinutes: number;
  bufferMinutes: number;
  timezone: string;
  hiringPanelEmployeeIds: string[];
  /** When set (Solo feature + allowlist), Google Meet is created with this employee's calendar */
  calendarOrganizerEmployeeId?: string;
  externalPanelEmails?: string[];
  specificTimeSlots?: SpecificTimeSlot[];
  businessHoursStart?: number;
  businessHoursEnd?: number;
  availableWeekdays?: number[];
}

// Response types
export interface BookingCandidateInfo {
  candidateId: number;
  candidateName: string;
  candidateEmail: string;
  bookingToken: string;
  bookingLink: string;
  status: 'PENDING' | 'SCHEDULED' | 'EXPIRED' | 'CANCELLED';
  scheduledAt?: string;
}

export interface HumanInterviewBookingResponse {
  id: string;
  jobId: string;
  jobTitle: string;
  organisationId: string;
  createdByEmployeeId: string;
  calendarOrganizerEmployeeId?: string;
  stageId?: string;
  stageName: string;
  availableFrom: string;
  availableTo: string;
  slotDurationMinutes: number;
  bufferMinutes: number;
  timezone: string;
  hiringPanelEmployeeIds: string[];
  externalPanelEmails?: string[];
  status: 'ACTIVE' | 'EXPIRED' | 'CANCELLED';
  expiresAt?: string;
  candidates: BookingCandidateInfo[];
  createdAt: string;
  updatedAt: string;
}

export interface CoordinatorOrganizerUiConfig {
  showOrganizerDropdown: boolean;
  coordinators: Array<{
    employeeId: string;
    email: string;
    name: string | null;
  }>;
}

/**
 * Feature flag + allowlisted employees who can host the coordination calendar (Solo env).
 */
export async function fetchCoordinatorOrganizerUiConfig(): Promise<CoordinatorOrganizerUiConfig> {
  const response = await fetch(`${API_BASE_URL}/human-interview-bookings/coordinator-ui-config`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  return handleAPIResponse<CoordinatorOrganizerUiConfig>(response);
}

/**
 * Create a new human interview booking
 */
export async function createHumanInterviewBooking(
  data: CreateHumanInterviewBookingRequest
): Promise<HumanInterviewBookingResponse> {
  const response = await fetch(`${API_BASE_URL}/human-interview-bookings`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });

  const result = await handleAPIResponse<HumanInterviewBookingResponse>(response);
  return result;
}

/**
 * Get booking details by ID
 */
export async function getHumanInterviewBooking(bookingId: string): Promise<HumanInterviewBookingResponse> {
  const response = await fetch(`${API_BASE_URL}/human-interview-bookings/${bookingId}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  const result = await handleAPIResponse<HumanInterviewBookingResponse>(response);
  return result;
}

// Public API endpoints (no authentication required)

export interface PublicBookingDetailsResponse {
  bookingId: string;
  jobTitle: string;
  companyName: string;
  companyLogo?: string;
  stageName: string;
  candidateName: string;
  candidateEmail: string;
  slotDurationMinutes: number;
  timezone: string;
  availableFrom: string;
  availableTo: string;
  status: 'PENDING' | 'SCHEDULED' | 'EXPIRED' | 'CANCELLED';
  scheduledAt?: string;
  conferenceLink?: string;
}

export interface TimeSlot {
  startTime: Date | string;
  endTime: Date | string;
  startTimeISO: string;
  endTimeISO: string;
  displayTime: string;
}

export interface AvailableSlotsResponse {
  slots: TimeSlot[];
  slotsByDate: Record<string, TimeSlot[]>;
  timezone: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Template APIs (per-job, per-stage configuration for 1:1 bookings)
// ─────────────────────────────────────────────────────────────────────────────

export interface HumanInterviewTemplateConfig {
  stageName: string;
  timezone: string;
  slotDurationMinutes: number;
  bufferMinutes: number;
  windowLengthDays: number;
  specificTimeSlots: SpecificTimeSlot[];
  requiredPanelEmployeeIds: string[];
  optionalPanelEmployeeIds: string[];
}

export interface HumanInterviewTemplateAutomationSettings {
  // NOTE: Stage-move automation is not wired up yet; this mirrors the backend
  // shape so we don't have to change the contract when we add it later.
  isStageAutoTriggerEnabled: boolean;
  triggerOn: 'enter';
  sendEmailImmediately: boolean;
}

export interface HumanInterviewTemplate {
  id: string;
  jobId: string;
  pipelineStageId: string;
  name: string;
  description?: string;
  defaultStageType?: string;
  automation?: HumanInterviewTemplateAutomationSettings;
  config: HumanInterviewTemplateConfig;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateHumanInterviewTemplateRequest {
  jobId: string;
  pipelineStageId: string;
  name: string;
  description?: string;
  config: HumanInterviewTemplateConfig;
}

export interface ScheduleConfirmationResponse {
  success: boolean;
  scheduleId: string;
  scheduledAt: string;
  conferenceLink: string;
  message: string;
}

/**
 * Get public booking details by token (no auth required)
 */
export async function fetchPublicBookingDetails(token: string): Promise<PublicBookingDetailsResponse> {
  const response = await fetch(`${API_BASE_URL}/public/human-interview-bookings/${token}`, {
    method: 'GET',
  });

  const result = await handleAPIResponse<PublicBookingDetailsResponse>(response);
  return result;
}

/**
 * Get available time slots (no auth required)
 */
export async function fetchAvailableSlots(token: string): Promise<AvailableSlotsResponse> {
  const response = await fetch(`${API_BASE_URL}/public/human-interview-bookings/${token}/available-slots`, {
    method: 'GET',
  });

  const result = await handleAPIResponse<AvailableSlotsResponse>(response);
  return result;
}

/**
 * Schedule an interview slot (no auth required)
 */
export async function scheduleInterviewSlot(
  token: string,
  slotStartTime: string
): Promise<ScheduleConfirmationResponse> {
  const response = await fetch(`${API_BASE_URL}/public/human-interview-bookings/${token}/schedule`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ slotStartTime }),
  });

  const result = await handleAPIResponse<ScheduleConfirmationResponse>(response);
  return result;
}

/**
 * Preview calendar-aware availability for a prospective human interview booking.
 * This uses the same slot calculation + Google Calendar free/busy intersection
 * as the candidate-facing availability endpoint, but without creating any records.
 */
export async function previewHumanInterviewAvailability(
  data: CreateHumanInterviewBookingRequest
): Promise<AvailableSlotsResponse> {
  const response = await fetch(`${API_BASE_URL}/human-interview-bookings/preview-availability`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });

  const result = await handleAPIResponse<AvailableSlotsResponse>(response);
  return result;
}

/**
 * Create a reusable human interview booking template for a specific job + stage.
 * This is invoked from the company app (authenticated) and stores the current
 * modal configuration so it can be reapplied later.
 */
export async function createHumanInterviewTemplate(
  payload: CreateHumanInterviewTemplateRequest
): Promise<HumanInterviewTemplate> {
  const { jobId, pipelineStageId, ...body } = payload;

  const response = await fetch(
    `${API_BASE_URL}/jobs/${jobId}/pipeline-stages/${pipelineStageId}/human-interview-templates`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    }
  );

  const result = await handleAPIResponse<{ data: HumanInterviewTemplate }>(response);
  return result.data;
}

/**
 * List all human interview booking templates for a given job + stage.
 * The booking modal will use this to populate the "Apply template" control.
 */
/**
 * Normalize template so config is always an object (API/DB may return config as JSON string).
 */
function normalizeTemplate(
  t: HumanInterviewTemplate & { config?: HumanInterviewTemplateConfig | string }
): HumanInterviewTemplate {
  const config =
    typeof t.config === 'string'
      ? (JSON.parse(t.config) as HumanInterviewTemplateConfig)
      : t.config;
  return { ...t, config } as HumanInterviewTemplate;
}

export async function listHumanInterviewTemplates(
  jobId: string,
  pipelineStageId: string
): Promise<HumanInterviewTemplate[]> {
  const response = await fetch(
    `${API_BASE_URL}/jobs/${jobId}/pipeline-stages/${pipelineStageId}/human-interview-templates`,
    {
      method: 'GET',
      headers: getAuthHeaders(),
    }
  );

  const data = await handleAPIResponse<(HumanInterviewTemplate & { config?: HumanInterviewTemplateConfig | string })[]>(response);
  return (Array.isArray(data) ? data : []).map(normalizeTemplate);
}
